import React, { useState } from 'react';
import { List, useTable, EditButton, DeleteButton } from '@refinedev/antd';
import { useCustomMutation, useNotification } from '@refinedev/core';
import { EditOutlined } from '@ant-design/icons';
import { Alert, Button, Input, InputNumber, Modal, Space, Table, Tag } from 'antd';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import type { ReferralPromoCodeDto } from '@wellverse/shared-ts';

/** Why a code would be refused right now, or null when it can be redeemed. */
const blockedReason = (code: ReferralPromoCodeDto): string | null => {
  if (!code.isActive) return 'Off';
  if (code.expiresAt && dayjs(code.expiresAt).isBefore(dayjs())) return 'Expired';
  if (code.maxRedemptions != null && code.redemptionCount >= code.maxRedemptions) return 'Used up';
  return null;
};

/**
 * Every referral code: the ones an admin creates, each worth its own number of
 * free days, followed by each member's own `WOOKI-` code. A member's code is
 * issued at sign-up and pays the `referral_free_days` setting unless it is
 * given its own number of days here; nothing else about it can be edited.
 */
export const ReferralCodeList: React.FC = () => {
  const [search, setSearch] = useState<string>('');
  // The member code whose days are being edited, and what is typed for it.
  const [editing, setEditing] = useState<ReferralPromoCodeDto | null>(null);
  const [days, setDays] = useState<number | null>(null);
  const { open } = useNotification();
  const { mutate, isLoading: isSaving } = useCustomMutation<ReferralPromoCodeDto>();

  const { tableProps, tableQueryResult } = useTable<ReferralPromoCodeDto>({
    resource: 'referral-codes',
    filters: {
      permanent: search ? [{ field: 'search', operator: 'eq' as const, value: search }] : [],
    },
    pagination: { current: 1, pageSize: 20 },
    queryOptions: { keepPreviousData: true },
  });

  const editMemberDays = (record: ReferralPromoCodeDto) => {
    setEditing(record);
    setDays(record.owner?.ownFreeDays ?? null);
  };

  const saveMemberDays = () => {
    if (!editing?.owner) return;
    mutate(
      {
        url: `/admin/referral-codes/members/${editing.owner.userId}`,
        method: 'patch',
        values: { freeDays: days },
      },
      {
        onSuccess: () => {
          open?.({ type: 'success', message: `Free days of ${editing.code} saved` });
          setEditing(null);
          tableQueryResult.refetch();
        },
        onError: (error) => {
          open?.({ type: 'error', message: error?.message || 'Could not save the free days' });
        },
      },
    );
  };

  return (
    <List resource="referral-codes" title="Referral Codes" canCreate>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Codes created here give the member who enters one the code's own number of free Premium days."
        description="Every member also has a personal WOOKI- code; those give both sides the number of days set in Settings → referral_free_days, unless the code is given its own number here. A change applies to referrals made afterwards; earlier ones keep their days."
      />
      <div style={{ marginBottom: 16 }}>
        <Input.Search
          placeholder="Search code"
          allowClear
          style={{ width: 240 }}
          onSearch={(value) => setSearch(value.trim())}
        />
      </div>
      <Table {...tableProps} rowKey="id" scroll={{ x: 1000 }}>
        <Table.Column<ReferralPromoCodeDto>
          dataIndex="code"
          title="Code"
          render={(code: string) => <strong>{code}</strong>}
        />
        <Table.Column<ReferralPromoCodeDto>
          title="Owner"
          render={(_, record) =>
            record.owner ? (
              <Link to={`/users/show/${record.owner.userId}`}>{record.owner.displayName}</Link>
            ) : (
              <Tag>Created in CMS</Tag>
            )
          }
        />
        <Table.Column<ReferralPromoCodeDto>
          dataIndex="freeDays"
          title="Free Days"
          render={(value: number, record) =>
            record.owner && record.owner.ownFreeDays == null ? (
              <Space size={6}>
                {`${value} days`}
                <Tag>Default</Tag>
              </Space>
            ) : (
              `${value} days`
            )
          }
        />
        <Table.Column<ReferralPromoCodeDto>
          title="Status"
          render={(_, record) => {
            const blocked = blockedReason(record);
            return <Tag color={blocked ? 'default' : 'green'}>{blocked ?? 'Active'}</Tag>;
          }}
        />
        <Table.Column<ReferralPromoCodeDto>
          title="Redeemed"
          render={(_, record) =>
            record.maxRedemptions != null
              ? `${record.redemptionCount} / ${record.maxRedemptions}`
              : record.redemptionCount
          }
        />
        <Table.Column<ReferralPromoCodeDto>
          dataIndex="expiresAt"
          title="Expires"
          render={(value: string | null) => (value ? dayjs(value).format('YYYY-MM-DD HH:mm') : '—')}
        />
        <Table.Column<ReferralPromoCodeDto> dataIndex="note" title="Note" ellipsis />
        <Table.Column<ReferralPromoCodeDto>
          title="Actions"
          fixed="right"
          render={(_, record) =>
            record.owner ? (
              <Button
                size="small"
                icon={<EditOutlined />}
                title="Set free days"
                onClick={() => editMemberDays(record)}
              />
            ) : (
              <Space>
                <EditButton hideText size="small" recordItemId={record.id} resource="referral-codes" />
                <DeleteButton hideText size="small" recordItemId={record.id} resource="referral-codes" />
              </Space>
            )
          }
        />
      </Table>
      <Modal
        title={editing ? `Free days of ${editing.code}` : ''}
        open={editing != null}
        okText="Save"
        confirmLoading={isSaving}
        onOk={saveMemberDays}
        onCancel={() => setEditing(null)}
        destroyOnClose
      >
        <p>
          Both the member and the friend who enters this code get this many free Premium days.
          Leave it empty to use the default from Settings. Referrals already made keep their days.
        </p>
        <InputNumber
          min={1}
          max={3650}
          precision={0}
          placeholder="Default"
          addonAfter="days"
          value={days}
          onChange={(value) => setDays(value ?? null)}
        />
      </Modal>
    </List>
  );
};
